"use client";

import { useEffect, useRef, useState } from "react";
import { useInView } from "motion/react";
import {
  MediaActionTypes,
  useMediaRef,
  useMediaDispatch,
  useMediaSelector,
} from "media-chrome/react/media-store";
import { MediaLoadingIndicator } from "media-chrome/react";
import HlsPlayer from "hls-video-element/react";
import type { HlsVideoElement } from "hls-video-element";

import { cn } from "@marketing-sites/utils/cn";
import { triggerAnalyticsEvent } from "@marketing-sites/analytics/trigger-analytics-event";
import { TrackingAction, TrackingType } from "@marketing-sites/analytics/types";

import { useMediaPlayerContext } from "./media-player.provider";
import type { VideoProps } from "./media-player.types";

const isAndroid = () =>
  typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);

/**
 * HLS video element.
 *
 * The video source, poster, storyboard, and captions come from `MediaPlayer.Context`
 * (what to play). Playback behavior — muted, autoplay, loop, and event callbacks —
 * are passed as props (how to play it).
 *
 * Analytics events (play, pause, end) are tracked automatically via
 * `@marketing-sites/analytics`, using the video id as the location.
 */
export const Video = ({
  analyticsId,
  muted,
  loop,
  autoplay,
  playsInline,
  onPlay,
  onPause,
  onEnded,
  className,
}: VideoProps) => {
  const mediaRefCallback = useMediaRef();
  const dispatch = useMediaDispatch();
  const [isPlaying, setIsPlaying] = useState(false);
  // hls.js wipes the cues of every text track when it attaches to the element.
  const [mediaLoadId, setMediaLoadId] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { video } = useMediaPlayerContext();
  const isInView = useInView(videoRef, { once: false, amount: 0.75 });
  const isNearViewport = useInView(videoRef, { margin: "200px", once: true });

  const time = useMediaSelector(
    (store) => +(store.mediaCurrentTime ?? 0)?.toFixed(0),
  );

  // Auto-pause when the video scrolls out of view.
  // Skipped on iOS because iOS handles this natively via the media session —
  // force-pausing via JS would conflict with iOS's mini-player / control center behavior.
  useEffect(() => {
    const isIPhone =
      typeof navigator !== "undefined" &&
      /iPhone|iPod|iPad/.test(navigator.userAgent);
    if (!isInView && isPlaying && !isIPhone) {
      setIsPlaying(false);
      dispatch({ type: MediaActionTypes.MEDIA_PAUSE_REQUEST });
    }
  }, [isInView, isPlaying, dispatch]);

  const handlePlay = (e: React.SyntheticEvent<HlsVideoElement>) => {
    setIsPlaying(true);
    onPlay?.(e);
    triggerAnalyticsEvent({
      action: TrackingAction.Play,
      type: TrackingType.Video,
      text: `Played from ${time}s`,
      // eslint-disable-next-line analytics/id-must-be-static -- analyticsId is a forwarded prop, so the rule has already checked the caller's literal; only the event-type suffix is added here
      analyticsId: `${analyticsId}_play`,
      location: video.id,
    });
  };

  const handlePause = (e: React.SyntheticEvent<HlsVideoElement>) => {
    setIsPlaying(false);

    // Android has limited playsInline support. If the user manually entered
    // fullscreen and then pauses, exit fullscreen as a safety net.
    if (isAndroid()) {
      dispatch({
        type: MediaActionTypes.MEDIA_EXIT_FULLSCREEN_REQUEST,
      });
    }

    onPause?.(e);
    triggerAnalyticsEvent({
      action: TrackingAction.Pause,
      type: TrackingType.Video,
      text: `Paused at ${time}s`,
      // eslint-disable-next-line analytics/id-must-be-static -- analyticsId is a forwarded prop, so the rule has already checked the caller's literal; only the event-type suffix is added here
      analyticsId: `${analyticsId}_pause`,
      location: video.id,
    });
  };

  const handleEnded = (e: React.SyntheticEvent<HlsVideoElement>) => {
    setIsPlaying(false);
    dispatch({ type: MediaActionTypes.MEDIA_PAUSE_REQUEST });
    onEnded?.(e);
    triggerAnalyticsEvent({
      action: TrackingAction.End,
      type: TrackingType.Video,
      text: "Video ended",
      // eslint-disable-next-line analytics/id-must-be-static -- analyticsId is a forwarded prop, so the rule has already checked the caller's literal; only the event-type suffix is added here
      analyticsId: `${analyticsId}_ended`,
      location: video.id,
    });
  };

  return (
    <>
      <HlsPlayer
        slot="media"
        ref={(el: HTMLVideoElement | null) => {
          mediaRefCallback(el);
          if (el) {
            videoRef.current = el;
          }
        }}
        src={isNearViewport ? video?.url : undefined}
        poster={isNearViewport ? video?.poster : undefined}
        crossOrigin=""
        className={cn(
          "@container z-(--z-index-slot-video) h-full w-full overflow-hidden bg-transparent",
          className,
        )}
        controls={false}
        // Default: true on Android (forces inline), false elsewhere (iOS plays fullscreen natively).
        // Override with playsInline prop to force inline on all devices.
        playsInline={playsInline ?? isAndroid()}
        autoplay={autoplay}
        defaultMuted={muted}
        loop={loop}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={handleEnded}
        onLoadStart={() => setMediaLoadId((id) => id + 1)}
      >
        {video?.storyboard && (
          <track
            label="thumbnails"
            default
            kind="metadata"
            src={video.storyboard}
          />
        )}
        {video?.captions && mediaLoadId > 0 && (
          <track
            key={mediaLoadId}
            default={video.captionsDefault}
            label="captions"
            kind="captions"
            src={video.captions}
          />
        )}
      </HlsPlayer>

      {/* Custom Loading Spinner */}
      <MediaLoadingIndicator
        slot="centered-chrome"
        noAutohide
      ></MediaLoadingIndicator>
    </>
  );
};
